/**
 * One master set or sub set row: the set's logo, its name, and a progress bar.
 *
 * The same row type serves both sections, because a sub set is a filter over a
 * master's slots rather than a set of its own - so Base Set as a full master
 * and Jungle as a 1st-Ed-only sub set are the same thing with a different
 * scope, and they sit side by side naturally.
 *
 * Progress is always slots filled over slots in scope, both counted by the
 * database. Nothing is derived from card_count_total here, which reads 102 for
 * a Base Set that is really 408 slots.
 */

import { useRef } from 'react';
import { Layers } from 'lucide-react';
import { holdProps } from './wishHold';

export default function WishSetRow({
  row,
  logo,
  progress,
  theme,
  readOnly = false,
  onOpen,
  onHold,
}) {
  const heldRef = useRef(false);
  const slots = Number(progress?.slots) || 0;
  // Null filled means nobody asked whose progress this is - somebody else's
  // list, where counting against the viewer's own cards would be a lie. The row
  // shows its size and no bar.
  const known = progress ? progress.filled !== null && progress.filled !== undefined : false;
  const filled = Number(progress?.filled) || 0;
  const pct = known && slots > 0 ? Math.min(100, Math.round((filled / slots) * 100)) : 0;

  return (
    <div
      style={{ ...styles.row, backgroundColor: theme.bgCard, borderColor: theme.border }}
      onClick={() => { if (!heldRef.current) onOpen?.(row); }}
      data-testid={`wish-set-row-${row.id}`}
      {...holdProps({ onHold: () => onHold?.(row), firedRef: heldRef, disabled: readOnly })}
      onContextMenu={(e) => { e.preventDefault(); if (!readOnly) onHold?.(row); }}
    >
      <div style={styles.logoBox}>
        {logo ? (
          // The catalogue's logo_url is extensionless by convention, and every
          // renderer in the app appends the extension itself.
          <img src={`${logo}.png`} alt={row.name} loading="lazy" style={styles.logo} />
        ) : (
          <Layers size={20} color={theme.textMuted} />
        )}
      </div>
      <div style={styles.body}>
        <span style={{ ...styles.name, color: theme.text }}>{row.name}</span>
        {known && (
          <div style={{ ...styles.track, backgroundColor: theme.border }}>
            <div style={{ ...styles.fill, width: `${pct}%`, backgroundColor: theme.warning }} />
          </div>
        )}
        <span style={{ ...styles.count, color: theme.textMuted }}>
          {!progress && 'Counting slots...'}
          {progress && known && `${filled} of ${slots} slots · ${pct}%`}
          {progress && !known && `${slots} slots`}
        </span>
      </div>
    </div>
  );
}

const styles = {
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    // A 60px-tall row is a comfortable thumb target in a list.
    minHeight: '64px',
    padding: '10px 12px',
    borderRadius: '12px',
    border: '1px solid',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  },
  logoBox: { width: '52px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  logo: { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' },
  body: { display: 'flex', flexDirection: 'column', gap: '5px', flex: 1, minWidth: 0 },
  name: { fontSize: '15px', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  track: { height: '6px', borderRadius: '3px', overflow: 'hidden' },
  fill: { height: '100%', borderRadius: '3px' },
  count: { fontSize: '12px' },
};
